import { useState, useEffect, useRef, type FormEvent, type DragEvent, type ChangeEvent } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import {
  usePerson, useUpdatePerson, useLinkPersonToOrg, useSetPersonPhoto, useDeletePersonPhoto,
  useAssignPersonRole,
} from '../api/queries/people';
import { useParseVcards } from '../api/queries/contactsIngest';
import { useCreateUser, useRbacRoles } from '../api/queries/settings';
import { useLookups, activeOptionsFor, useLookupLabel } from '../api/queries/lookups';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import { PhoneListInput, type PhoneEntry } from '../components/PhoneListInput';
import { PersonAvatar } from '../components/PersonAvatar';
import { PhotoCropModal } from '../components/PhotoCropModal';
import {
  EmailListInput, emailsToPayload, emailsFromPerson, type EmailEntry,
} from '../components/EmailListInput';
import {
  AddressListInput, addressesToPayload, addressesFromPerson, type AddressEntry,
} from '../components/AddressListInput';
import { OrgPicker } from '../components/OrgPicker';
import {
  ClientRoleEditor, VendorRoleEditor, ContractorRoleEditor, AssignRolesSection,
} from '../components/RoleEditors';
import { IconAction } from '../components/IconAction';
import { addressValueFromColumns, displayCountry } from '../utils/address';
import { stripPhoneForStorage, formatPhoneDisplay, formatPhoneAsTyped } from '../utils/phone';
import type { ParsedVcard, RoleBadge } from '@stfors/shared-types';
import './DirectoryPage.css';

function base64ToFile(base64: string, mime: string): File {
  const bin = atob(base64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new File([arr], 'photo', { type: mime });
}

// Raster types the photo endpoint accepts (mirrors the server allowlist; SVG is
// excluded on purpose). HEIC and other unsupported picks are caught here too.
const PHOTO_MIMES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

export function PersonDetailPage(): JSX.Element {
  const navigate = useNavigate();
  const params = useParams();
  const id = Number(params['id']);
  const { data, isPending, error } = usePerson(Number.isInteger(id) ? id : null);
  const update = useUpdatePerson(id);
  const link = useLinkPersonToOrg();
  const parse = useParseVcards();
  const setPhoto = useSetPersonPhoto(id);
  const deletePhoto = useDeletePersonPhoto(id);
  const location = useLocation();
  const createUser = useCreateUser();
  const assignRole = useAssignPersonRole();
  const canManageUsers = can('settings.manage_users');
  const rbacRoles = useRbacRoles();
  const roleOptions = rbacRoles.data?.roles ?? [];

  const lookups = useLookups();
  const titleOptions = activeOptionsFor(lookups.data?.lookup_options, 'title');
  const suffixOptions = activeOptionsFor(lookups.data?.lookup_options, 'suffix');
  const phoneTypeOptions = activeOptionsFor(lookups.data?.lookup_options, 'phone_type');
  const emailTypeOptions = activeOptionsFor(lookups.data?.lookup_options, 'email_type');
  const addressTypeOptions = activeOptionsFor(lookups.data?.lookup_options, 'address_type');
  const roleLabel = useLookupLabel('contact_role');

  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState('');
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [suffix, setSuffix] = useState('');
  const [preferred, setPreferred] = useState('');
  const [stagedPhoto, setStagedPhoto] = useState<{ base64: string; mime: string } | null>(null);
  // Manual photo editor: a picked file awaiting crop, a version bump to refresh
  // the avatar after a replace (where has_photo doesn't change), and an error line.
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [photoVersion, setPhotoVersion] = useState(0);
  const [photoErr, setPhotoErr] = useState<string | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [vcardMsg, setVcardMsg] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const vcardOnceRef = useRef(false);
  const [phones, setPhones] = useState<PhoneEntry[]>([]);
  const [emails, setEmails] = useState<EmailEntry[]>([]);
  const [addresses, setAddresses] = useState<AddressEntry[]>([]);
  const [notes, setNotes] = useState('');
  const [editError, setEditError] = useState<string | null>(null);

  // "Link to company" inline picker state.
  const [linking, setLinking] = useState(false);
  const [linkOrgId, setLinkOrgId] = useState<number | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);

  const onLink = async () => {
    if (linkOrgId === null) return;
    setLinkError(null);
    try {
      await link.mutateAsync({ orgId: linkOrgId, person_id: id });
      setLinking(false);
      setLinkOrgId(null);
    } catch (err) {
      setLinkError(err instanceof ApiError ? err.message : 'Could not link. Try again.');
    }
  };

  // "Make staff" — give this person a login account (settings.manage_users only).
  const [staffing, setStaffing] = useState(false);
  const [staffEmail, setStaffEmail] = useState('');
  const [staffPassword, setStaffPassword] = useState('');
  const [staffRoleNames, setStaffRoleNames] = useState<string[]>([]);
  const [staffError, setStaffError] = useState<string | null>(null);

  const toggleStaffRole = (name: string) =>
    setStaffRoleNames((prev) => (prev.includes(name) ? prev.filter((r) => r !== name) : [...prev, name]));

  const onMakeStaff = async () => {
    setStaffError(null);
    if (staffEmail.trim().length === 0) { setStaffError('A login email is required.'); return; }
    if (staffPassword.length < 12) { setStaffError('Password must be at least 12 characters.'); return; }
    if (staffRoleNames.length === 0) { setStaffError('Pick at least one access role.'); return; }
    try {
      await createUser.mutateAsync({
        person_id: id,
        email: staffEmail.trim(),
        password: staffPassword,
        display_name: data!.person.display_name,
        role_names: staffRoleNames,
      });
      setStaffing(false);
      setStaffEmail(''); setStaffPassword(''); setStaffRoleNames([]);
    } catch (err) {
      setStaffError(err instanceof ApiError ? err.message : 'Could not create the login. Try again.');
    }
  };

  // Merge a parsed vCard into the edit form: fill blank name fields, append
  // new phones/emails/addresses, stage the photo. Works whether or not we're
  // already editing (when not, the merge seeds from the saved person first).
  const mergeCardIntoForm = (p: ParsedVcard) => {
    if (!data) return;
    const ppl = data.person;
    setTitle((prev) => { const cur = editing ? prev : (ppl.title ?? ''); return cur.trim() ? cur : (p.prefix ?? cur); });
    setFirst((prev) => { const cur = editing ? prev : (ppl.first_name ?? ''); return cur.trim() ? cur : (p.first_name ?? cur); });
    setLast((prev) => { const cur = editing ? prev : (ppl.last_name ?? ''); return cur.trim() ? cur : (p.last_name ?? cur); });
    setSuffix((prev) => { const cur = editing ? prev : (ppl.suffix ?? ''); return cur.trim() ? cur : (p.suffix ?? cur); });
    setPreferred((prev) => { const cur = editing ? prev : (ppl.preferred_name ?? ''); return cur.trim() ? cur : (p.nickname ?? cur); });
    setNotes((prev) => (editing ? prev : (ppl.notes ?? '')));
    setPhones((prev) => {
      const cur = editing ? prev : ppl.phones.map((ph) => ({ phone_type: ph.phone_type, phone: formatPhoneAsTyped(ph.phone) }));
      const have = new Set(cur.map((ph) => stripPhoneForStorage(ph.phone)));
      const extra = p.phones
        .filter((a) => { const ten = stripPhoneForStorage(a.phone); return !!ten && ten.length === 10 && !have.has(ten); })
        .map((a) => ({ phone_type: a.phone_type, phone: a.phone }));
      return [...cur, ...extra];
    });
    setEmails((prev) => {
      const cur = editing ? prev : emailsFromPerson(ppl.emails);
      const have = new Set(cur.map((e) => e.email.toLowerCase()));
      const extra = p.emails.filter((a) => !have.has(a.email.toLowerCase())).map((a) => ({ label: a.label ?? '', email: a.email }));
      return [...cur, ...extra];
    });
    setAddresses((prev) => {
      const cur = editing ? prev : addressesFromPerson(ppl.addresses);
      if (!p.address) return cur;
      const entry: AddressEntry = { label: '', ...addressValueFromColumns(p.address) };
      const dupe = cur.some((c) => c.streetName && entry.streetName && c.streetName.toLowerCase() === entry.streetName.toLowerCase() && c.postal_code === entry.postal_code);
      return dupe ? cur : [...cur, entry];
    });
    if (p.photo) setStagedPhoto(p.photo);
    setEditError(null);
    setEditing(true);
  };

  const handleVcardFile = async (filename: string, content: string) => {
    setVcardMsg(null);
    try {
      const res = await parse.mutateAsync([{ filename, content }]);
      const card = res.cards.find((c) => c.parsed);
      if (!card?.parsed) { setVcardMsg('No contact card found in that file.'); return; }
      mergeCardIntoForm(card.parsed);
      const more = res.cards.filter((c) => c.parsed).length - 1;
      setVcardMsg(`Filled from ${filename} — review and Save.${more > 0 ? ` (${more} more card(s) ignored.)` : ''}`);
    } catch (err) {
      setVcardMsg(err instanceof ApiError ? err.message : 'Could not read that file.');
    }
  };

  const onVcardDrop = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    setDragOver(false);
    const file = Array.from(e.dataTransfer.files).find((f) => /\.vcf$/i.test(f.name) || f.type === 'text/vcard');
    if (file) void file.text().then((txt) => handleVcardFile(file.name, txt));
  };

  // A .vcf dropped on a Directory row navigates here with the text in router
  // state — pick it up once on arrival and run the same fill.
  useEffect(() => {
    if (!data || vcardOnceRef.current) return;
    const st = location.state as { vcardText?: string; vcardName?: string } | null;
    if (st?.vcardText) {
      vcardOnceRef.current = true;
      void handleVcardFile(st.vcardName ?? 'contact.vcf', st.vcardText);
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, location.state]);

  if (isPending) return <p className="directory-status">Loading…</p>;
  if (error || !data) {
    return (
      <p className="directory-status directory-error">
        {error instanceof ApiError && error.status === 404 ? 'Person not found.' : 'Failed to load the person.'}
      </p>
    );
  }

  const { person, roles, organizations } = data;

  const startEdit = () => {
    setTitle(person.title ?? '');
    setFirst(person.first_name ?? '');
    setLast(person.last_name ?? '');
    setSuffix(person.suffix ?? '');
    setPreferred(person.preferred_name ?? '');
    setPhones(person.phones.map((p) => ({ phone_type: p.phone_type, phone: formatPhoneAsTyped(p.phone) })));
    setEmails(emailsFromPerson(person.emails));
    setAddresses(addressesFromPerson(person.addresses));
    setNotes(person.notes ?? '');
    setStagedPhoto(null);
    setVcardMsg(null);
    setEditError(null);
    setEditing(true);
  };

  const onSave = async (e: FormEvent) => {
    e.preventDefault();
    setEditError(null);
    if (first.trim().length === 0 && last.trim().length === 0) {
      setEditError('A first or last name is required.');
      return;
    }
    const phonesPayload = phones
      .map((p) => ({ phone_type: p.phone_type, phone: stripPhoneForStorage(p.phone) ?? '' }))
      .filter((p) => p.phone.length === 10 && p.phone_type.length > 0);
    try {
      await update.mutateAsync({
        title: title.trim() || null,
        first_name: first.trim() || null,
        last_name: last.trim() || null,
        suffix: suffix.trim() || null,
        preferred_name: preferred.trim() || null,
        notes: notes.trim() || null,
        phones: phonesPayload,
        emails: emailsToPayload(emails),
        addresses: addressesToPayload(addresses),
      });
      // Persist a staged photo (from a dropped vCard) after the row saves.
      if (stagedPhoto) {
        try { await setPhoto.mutateAsync(base64ToFile(stagedPhoto.base64, stagedPhoto.mime)); } catch { /* non-fatal */ }
        setStagedPhoto(null);
      }
      setVcardMsg(null);
      setEditing(false);
    } catch (err) {
      setEditError(err instanceof ApiError ? err.message : 'Could not save. Try again.');
    }
  };

  // Manual photo editor — pick → crop/zoom → upload immediately (independent of
  // the name Edit/Save form and the vCard staging path).
  const onPickPhoto = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    e.target.value = ''; // let the same file be re-picked
    if (!f) return;
    setPhotoErr(null);
    if (!PHOTO_MIMES.includes(f.type)) {
      setPhotoErr('Choose a JPEG, PNG, GIF, or WebP image.');
      return;
    }
    setPendingFile(f);
  };

  const onCropConfirm = async (cropped: File) => {
    try {
      await setPhoto.mutateAsync(cropped);
      setPhotoVersion((v) => v + 1);
      setPendingFile(null);
    } catch (err) {
      setPhotoErr(err instanceof ApiError ? err.message : 'Could not save the photo. Try again.');
      setPendingFile(null);
    }
  };

  const onRemovePhoto = async () => {
    if (!window.confirm('Remove this contact photo?')) return;
    setPhotoErr(null);
    try {
      await deletePhoto.mutateAsync();
      setPhotoVersion((v) => v + 1);
    } catch (err) {
      setPhotoErr(err instanceof ApiError ? err.message : 'Could not remove the photo. Try again.');
    }
  };

  const roleBadges: JSX.Element[] = [];
  if (roles.client_id !== null) {
    roleBadges.push(
      <Link key="client" to={`/clients/${roles.client_id}`} className="role-badge role-client">
        {roleLabel('client') || 'Client'}
      </Link>,
    );
  }
  if (roles.contractor_id !== null) {
    roleBadges.push(
      <Link key="contractor" to="/contractors" className="role-badge role-contractor">
        {roleLabel('contractor') || 'Contractor'}
      </Link>,
    );
  }
  if (roles.vendor_id !== null) {
    roleBadges.push(
      <span key="vendor" className="role-badge role-vendor">
        {roleLabel('vendor') || 'Vendor'}
      </span>,
    );
  }
  if (organizations.length > 0) {
    roleBadges.push(
      <span key="contact" className="role-badge role-contact">
        {roleLabel('contact') || 'Contact'}
      </span>,
    );
  }
  if (data.is_staff) {
    roleBadges.push(<span key="staff" className="role-badge role-staff">Staff</span>);
  }

  return (
    <section
      className={`section-shell ${dragOver ? 'pdp-dragover' : ''}`}
      onDragOver={(e) => { if (can('organization.write')) { e.preventDefault(); setDragOver(true); } }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { if (can('organization.write')) onVcardDrop(e); }}
    >
      <header className="section-header">
        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
          <div className="pdp-avatar-block">
            {editing && stagedPhoto ? (
              <img
                className="person-avatar"
                style={{ width: 56, height: 56 }}
                src={`data:${stagedPhoto.mime};base64,${stagedPhoto.base64}`}
                alt="staged"
              />
            ) : (
              <PersonAvatar
                personId={person.id}
                hasPhoto={data!.has_photo}
                name={person.display_name}
                size={56}
                refreshKey={photoVersion}
              />
            )}
            {can('organization.write') && (
              <div className="pdp-photo-actions">
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  hidden
                  onChange={onPickPhoto}
                />
                <button
                  type="button"
                  className="btn-text pdp-photo-btn"
                  onClick={() => photoInputRef.current?.click()}
                  disabled={setPhoto.isPending || deletePhoto.isPending}
                >
                  {data!.has_photo ? 'Change' : 'Add photo'}
                </button>
                {data!.has_photo && (
                  <button
                    type="button"
                    className="btn-text pdp-photo-btn"
                    onClick={onRemovePhoto}
                    disabled={setPhoto.isPending || deletePhoto.isPending}
                  >
                    Remove
                  </button>
                )}
              </div>
            )}
          </div>
          <div>
          <h2>
            {person.display_name}
            {person.preferred_name && <span className="pdp-preferred"> ({person.preferred_name})</span>}
          </h2>
          <div className="directory-roles" style={{ marginTop: 6 }}>
            <span className="entity-chip entity-chip-individual">Individual</span>
            {roleBadges.length > 0 ? roleBadges : <span className="directory-norole">No roles</span>}
          </div>
          </div>
        </div>
        <div className="directory-header-actions">
          {can('organization.write') && !editing && (
            <IconAction action="edit" onClick={startEdit} />
          )}
          <button type="button" className="btn-text" onClick={() => navigate('/directory')}>
            ← Back to directory
          </button>
        </div>
      </header>

      <div className="section-body">
        {photoErr && (
          <p className="new-client-error" role="alert" style={{ marginBottom: 'var(--space-2)' }}>{photoErr}</p>
        )}
        {can('organization.write') && (vcardMsg || !editing) && (
          <p className={`pdp-vcard-hint ${vcardMsg ? 'pdp-vcard-active' : ''}`}>
            {vcardMsg ?? 'Tip: drop a .vcf contact card here to fill this person from it.'}
          </p>
        )}
        {editing ? (
          <form className="new-client-form" onSubmit={onSave} noValidate>
            <div className="new-client-row">
              <label className="new-client-field new-client-field-narrow">
                <span>Title</span>
                <select className="form-input" value={title} onChange={(e) => setTitle(e.target.value)}>
                  <option value="">—</option>
                  {titleOptions.map((o) => (<option key={o.id} value={o.value}>{o.value}</option>))}
                  {title && !titleOptions.some((o) => o.value === title) && <option value={title}>{title} (legacy)</option>}
                </select>
              </label>
              <label className="new-client-field">
                <span>First name</span>
                <input className="form-input" value={first} onChange={(e) => setFirst(e.target.value)} maxLength={100} />
              </label>
              <label className="new-client-field">
                <span>Last name</span>
                <input className="form-input" value={last} onChange={(e) => setLast(e.target.value)} maxLength={100} />
              </label>
              <label className="new-client-field new-client-field-narrow">
                <span>Suffix</span>
                <select className="form-input" value={suffix} onChange={(e) => setSuffix(e.target.value)}>
                  <option value="">—</option>
                  {suffixOptions.map((o) => (<option key={o.id} value={o.value}>{o.value}</option>))}
                  {suffix && !suffixOptions.some((o) => o.value === suffix) && <option value={suffix}>{suffix} (legacy)</option>}
                </select>
              </label>
            </div>
            <label className="new-client-field new-client-field-full">
              <span>Preferred name <span className="pdp-field-hint">(casual name, e.g. “Greg” — shown on the profile only)</span></span>
              <input className="form-input" value={preferred} onChange={(e) => setPreferred(e.target.value)} maxLength={100} placeholder="optional" />
            </label>
            <div className="new-client-field new-client-field-full">
              <span className="new-client-section-label">Phone numbers</span>
              <PhoneListInput value={phones} onChange={setPhones} typeOptions={phoneTypeOptions} disabled={update.isPending} />
            </div>
            <div className="new-client-field new-client-field-full">
              <span className="new-client-section-label">Emails</span>
              <EmailListInput value={emails} onChange={setEmails} typeOptions={emailTypeOptions} disabled={update.isPending} />
            </div>
            <div className="new-client-field new-client-field-full">
              <span className="new-client-section-label">Addresses</span>
              <AddressListInput value={addresses} onChange={setAddresses} typeOptions={addressTypeOptions} disabled={update.isPending} />
            </div>
            <label className="new-client-field new-client-field-full">
              <span className="new-client-section-label">Notes</span>
              <textarea className="form-input new-client-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={10_000} />
            </label>
            {editError && <p className="new-client-error" role="alert">{editError}</p>}
            <div className="new-client-actions">
              <IconAction
                action="cancel"
                onClick={() => setEditing(false)}
                disabled={update.isPending}
              />
              <IconAction
                action="save"
                type="submit"
                disabled={update.isPending}
                label={update.isPending ? 'Saving…' : 'Save'}
              />
            </div>
          </form>
        ) : (
          <div className="org-detail-meta">
            {person.phones.length === 0 ? (
              <div>
                <div className="org-meta-label">Phone</div>
                <div className="org-meta-value">—</div>
              </div>
            ) : (
              person.phones.map((p) => (
                <div key={p.id}>
                  <div className="org-meta-label">{p.phone_type}{p.is_primary ? ' (primary)' : ''}</div>
                  <div className="org-meta-value">{formatPhoneDisplay(p.phone)}</div>
                </div>
              ))
            )}
            {person.emails.map((e) => (
              <div key={`email-${e.id}`}>
                <div className="org-meta-label">{e.label || 'Email'}{e.is_primary ? ' (primary)' : ''}</div>
                <div className="org-meta-value"><a href={`mailto:${e.email}`}>{e.email}</a></div>
              </div>
            ))}
            {person.addresses.map((a) => {
              const parts = [a.line1, a.line2, [a.city, a.state, a.postal_code].filter(Boolean).join(', '), displayCountry(a.country)]
                .filter((s) => s && s.length > 0);
              return (
                <div key={`addr-${a.id}`}>
                  <div className="org-meta-label">{a.label || 'Address'}{a.is_primary ? ' (primary)' : ''}</div>
                  <div className="org-meta-value" style={{ whiteSpace: 'pre-wrap' }}>{parts.join('\n') || '—'}</div>
                </div>
              );
            })}
            {person.notes && (
              <div style={{ gridColumn: '1 / -1' }}>
                <div className="org-meta-label">Notes</div>
                <div className="org-meta-value" style={{ whiteSpace: 'pre-wrap' }}>{person.notes}</div>
              </div>
            )}
          </div>
        )}

        {roles.client_id !== null && <ClientRoleEditor clientId={roles.client_id} />}
        {roles.vendor_id !== null && <VendorRoleEditor vendorId={roles.vendor_id} />}
        {roles.contractor_id !== null && <ContractorRoleEditor contractorId={roles.contractor_id} />}

        <AssignRolesSection
          heldRoles={(
            [
              roles.client_id !== null ? 'client' : null,
              roles.vendor_id !== null ? 'vendor' : null,
              roles.contractor_id !== null ? 'contractor' : null,
              organizations.length > 0 ? 'contact' : null,
            ] as const
          ).filter((r): r is RoleBadge => r !== null)}
          defaultEmail={person.emails.find((e) => e.is_primary)?.email
            ?? person.emails[0]?.email
            ?? ''}
          busy={assignRole.isPending}
          onAssign={async (role, opts) => {
            await assignRole.mutateAsync({
              personId: id,
              role,
              email: opts?.email,
              organization_id: opts?.organizationId,
            });
          }}
        />

        <div className="org-section-header" style={{ marginTop: 'var(--space-6)' }}>
          <h3>Company Affiliations</h3>
          {can('organization.write') && !linking && (
            <button type="button" className="btn-cyan-primary" onClick={() => { setLinking(true); setLinkError(null); }}>
              Link to company
            </button>
          )}
        </div>

        {linking && (
          <div className="new-client-form" style={{ padding: 0, marginBottom: 'var(--space-4)' }}>
            <div className="new-client-field new-client-field-full">
              <span className="new-client-section-label">Company</span>
              <OrgPicker value={linkOrgId} onChange={(oid) => setLinkOrgId(oid)} disabled={link.isPending} />
            </div>
            {linkError && <p className="new-client-error" role="alert">{linkError}</p>}
            <div className="new-client-actions">
              <button
                type="button"
                className="btn-charcoal-secondary"
                onClick={() => { setLinking(false); setLinkOrgId(null); setLinkError(null); }}
                disabled={link.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-cyan-primary"
                onClick={onLink}
                disabled={link.isPending || linkOrgId === null}
              >
                {link.isPending ? 'Linking…' : 'Link'}
              </button>
            </div>
          </div>
        )}

        {organizations.length === 0 ? (
          <p className="org-meta-label">Not a contact at any company.</p>
        ) : (
          <ul className="org-contacts-list">
            {organizations.map((o) => (
              <li key={o.organization_id} className="org-contact-row">
                <div>
                  <Link to={`/organizations/${o.organization_id}`} className="org-contact-name">{o.organization_name}</Link>
                  {o.is_primary && <span className="org-contact-primary">Primary</span>}
                  {o.title && <span className="org-contact-meta"> · {o.title}</span>}
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* Don't offer staff access to clients ("I don't want to make clients
            staff"); keep it for contractors / role-less. A client that's
            already staff still shows the read-only note below. */}
        {canManageUsers && (data.is_staff || roles.client_id === null) && (
          <>
            <div className="org-section-header">
              <h3>Login Account</h3>
              {!data.is_staff && !staffing && (
                <button type="button" className="btn-cyan-primary" onClick={() => { setStaffing(true); setStaffError(null); }}>
                  Make staff
                </button>
              )}
            </div>

            {data.is_staff ? (
              <p className="org-meta-label">
                This person has a login account (Staff).{' '}
                <Link to="/system-settings?tab=users">Manage it under Settings → Users</Link>.
              </p>
            ) : staffing ? (
              <div className="new-client-form" style={{ padding: 0 }}>
                <div className="new-client-row">
                  <label className="new-client-field">
                    <span>Login email *</span>
                    <input className="form-input" type="email" value={staffEmail} onChange={(e) => setStaffEmail(e.target.value)} maxLength={254} />
                  </label>
                  <label className="new-client-field">
                    <span>Initial password * (≥12)</span>
                    <input className="form-input" type="text" value={staffPassword} onChange={(e) => setStaffPassword(e.target.value)} minLength={12} />
                  </label>
                </div>
                <div className="new-client-field new-client-field-full">
                  <span className="new-client-section-label">Access roles *</span>
                  <div className="role-pills" role="group" aria-label="Access roles">
                    {roleOptions.map((r) => {
                      const selected = staffRoleNames.includes(r.name);
                      return (
                        <button
                          key={r.id}
                          type="button"
                          className={`role-pill role-pill-generic${selected ? ' role-pill-selected' : ''}`}
                          aria-pressed={selected}
                          onClick={() => toggleStaffRole(r.name)}
                          disabled={createUser.isPending}
                          title={r.description ?? undefined}
                        >
                          {r.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {staffError && <p className="new-client-error" role="alert">{staffError}</p>}
                <div className="new-client-actions">
                  <button type="button" className="btn-charcoal-secondary" onClick={() => { setStaffing(false); setStaffError(null); }} disabled={createUser.isPending}>
                    Cancel
                  </button>
                  <button type="button" className="btn-cyan-primary" onClick={onMakeStaff} disabled={createUser.isPending}>
                    {createUser.isPending ? 'Creating…' : 'Create login'}
                  </button>
                </div>
              </div>
            ) : (
              <p className="org-meta-label">No login account. Use “Make staff” to grant dashboard access.</p>
            )}
          </>
        )}
      </div>

      {pendingFile && (
        <PhotoCropModal
          file={pendingFile}
          open
          busy={setPhoto.isPending}
          onCancel={() => { if (!setPhoto.isPending) setPendingFile(null); }}
          onConfirm={onCropConfirm}
        />
      )}
    </section>
  );
}
